import { useNavigate } from 'react-router-dom';
import { BackButton, Badge, Card, EmptyState, PageHeader, Skeleton, SkeletonTable, type GenericBadgeVariant } from '@doccareplus/ui';
import { useTranslation } from '@doccareplus/shared/lib';
import { SUPPORT_EMAIL } from '@/config';
import { useBillingHistoryQuery, useSubscriptionQuery } from '../application/clinicAccess.query';

/**
 * The plan/status/commercial-terms skeleton — mirrors the header card's shape
 * (plan name + status pill, then a price/renewal row).
 */
function SubscriptionSummarySkeleton() {
  return (
    <Card className="p-6" aria-hidden>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-5 w-32" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <div className="mt-5 grid gap-4 border-t border-[var(--border)] pt-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4.5 w-24" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4.5 w-28" />
        </div>
      </div>
    </Card>
  );
}

function statusVariant(status: string): GenericBadgeVariant {
  switch (status.toLowerCase()) {
    case 'active':
    case 'trialing':
    case 'paid':
      return 'success';
    case 'pastdue':
    case 'pendingpayment':
    case 'pending':
      return 'warning';
    case 'suspended':
    case 'cancelled':
    case 'failed':
      return 'danger';
    case 'refunded':
      return 'warning';
    default:
      return 'default';
  }
}

function money(currency: string | null | undefined, amount: number | null | undefined) {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${currency ?? ''} ${amount}`.trim();
  }
}

function formattedDate(value: string | null | undefined) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(value));
  } catch {
    return null;
  }
}

/**
 * Clinic-facing subscription management page — real plan/status/commercial-terms detail plus a support
 * contact, replacing the previous "Coming soon" stub (see FE-3).
 */
export function SubscriptionManagementPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const query = useSubscriptionQuery();
  const billingQuery = useBillingHistoryQuery();
  const subscription = query.data;
  const billingRecords = billingQuery.data ?? [];

  if (query.isPending) {
    const loadingLabel = t('subscription.manage.title');
    return (
      <div className="page-wrap">
        <BackButton label={t('actions.back')} onClick={() => navigate('/mobile/more')} />
        <PageHeader title={t('subscription.manage.title')} />
        <div role="status" aria-label={loadingLabel}>
          <span className="sr-only">{loadingLabel}</span>
          <SubscriptionSummarySkeleton />
        </div>
      </div>
    );
  }

  if (query.isError || !subscription) {
    return (
      <div className="page-wrap">
        <BackButton label={t('actions.back')} onClick={() => navigate('/mobile/more')} />
        <PageHeader title={t('subscription.manage.title')} />
        <Card className="p-6 text-sm text-[var(--text-2)]">{t('subscription.manage.loadError')}</Card>
      </div>
    );
  }

  const listPrice = money(subscription.currency, subscription.listPrice);
  const finalPrice = money(subscription.currency, subscription.finalPrice);
  const hasDiscount =
    subscription.discountAmount != null && subscription.discountAmount > 0 && listPrice != null && finalPrice != null;
  const trialEndsAt = formattedDate(subscription.trialEndsAt);
  const renewsAt = formattedDate(subscription.currentPeriodEnd);

  return (
    <div className="page-wrap">
      <BackButton label={t('actions.back')} onClick={() => navigate('/mobile/more')} />
      <PageHeader title={t('subscription.manage.title')} subtitle={t('subscription.manage.subtitle')} />

      <Card className="mb-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] font-semibold text-[var(--text-3)]">{t('subscription.manage.plan')}</div>
            <div className="text-[20px] font-bold text-[var(--text)]">{subscription.planName}</div>
          </div>
          <Badge variant={statusVariant(subscription.status)}>{subscription.status}</Badge>
        </div>

        {(listPrice || subscription.billingInterval) && (
          <div className="mt-5 grid gap-4 border-t border-[var(--border)] pt-5 sm:grid-cols-2">
            {listPrice && (
              <div>
                <div className="text-[12.5px] font-semibold text-[var(--text-3)]">{t('subscription.manage.price')}</div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  {hasDiscount && <span className="text-sm text-[var(--text-3)] line-through">{listPrice}</span>}
                  <span className="text-[18px] font-bold text-[var(--text)]">{hasDiscount ? finalPrice : listPrice}</span>
                  {subscription.billingInterval && (
                    <span className="text-[12.5px] text-[var(--text-3)]">/{subscription.billingInterval.toLowerCase()}</span>
                  )}
                </div>
                {hasDiscount && subscription.discountPercentage != null && (
                  <div className="mt-1 text-[12px] font-medium text-[var(--success)]">
                    {t('subscription.manage.discountApplied', {
                      percent: subscription.discountPercentage,
                      cycles: subscription.discountCyclesRemaining ?? 0,
                    })}
                  </div>
                )}
              </div>
            )}
            {trialEndsAt && (
              <div>
                <div className="text-[12.5px] font-semibold text-[var(--text-3)]">{t('subscription.manage.trialEnds')}</div>
                <div className="mt-0.5 text-[14px] font-semibold text-[var(--text)]">{trialEndsAt}</div>
              </div>
            )}
            {!trialEndsAt && renewsAt && (
              <div>
                <div className="text-[12.5px] font-semibold text-[var(--text-3)]">{t('subscription.manage.renewsOn')}</div>
                <div className="mt-0.5 text-[14px] font-semibold text-[var(--text)]">{renewsAt}</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="mb-4 overflow-hidden">
        <div className="p-6 pb-0">
          <div className="text-[13.5px] font-semibold text-[var(--text)]">{t('subscription.manage.billingHistoryTitle')}</div>
        </div>
        {billingQuery.isPending ? (
          <div role="status" aria-label={t('subscription.manage.billingHistoryTitle')} className="p-6 pt-3">
            <span className="sr-only">{t('subscription.manage.billingHistoryTitle')}</span>
            <SkeletonTable rows={4} columns={4} />
          </div>
        ) : billingRecords.length === 0 ? (
          <EmptyState title={t('subscription.manage.billingHistoryEmpty')} className="border-0 p-6 shadow-none" />
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-t border-[var(--border)]">
                  <th className="px-6 py-2 font-semibold text-[var(--text-3)]">{t('subscription.manage.billingDescription')}</th>
                  <th className="px-6 py-2 font-semibold text-[var(--text-3)]">{t('subscription.manage.billingAmount')}</th>
                  <th className="px-6 py-2 font-semibold text-[var(--text-3)]">{t('subscription.manage.billingStatus')}</th>
                  <th className="px-6 py-2 font-semibold text-[var(--text-3)]">{t('subscription.manage.billingIssued')}</th>
                </tr>
              </thead>
              <tbody>
                {billingRecords.map((record) => (
                  <tr key={record.id} className="border-t border-[var(--border)]">
                    <td className="px-6 py-3">
                      <div className="font-medium text-[var(--text)]">{record.description}</div>
                      {record.referenceNumber && (
                        <div className="text-xs text-[var(--text-3)]">{record.referenceNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">{money(record.currency, record.amount)}</td>
                    <td className="px-6 py-3">
                      <Badge variant={statusVariant(record.status)}>{record.status}</Badge>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-[var(--text-2)]">
                      {formattedDate(record.issuedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="text-[13.5px] font-semibold text-[var(--text)]">{t('subscription.manage.helpTitle')}</div>
        <p className="mt-1 text-[13px] text-[var(--text-2)]">{t('subscription.manage.helpBody')}</p>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="mt-2 inline-block text-[13.5px] font-semibold text-[var(--primary)] underline"
        >
          {SUPPORT_EMAIL}
        </a>
      </Card>
    </div>
  );
}
